import React, { useState } from 'react';
import {
  Target,
  Zap,
  Star,
  Brain,
  LineChart,
  CheckCircle,
  TrendingUp,
  Timer,
  Award,
  Lightbulb,
  BarChart3,
  Activity
} from 'lucide-react';
import ChartsComponent from '../ChartsComponent';

const Analytics = () => {
  const [activeTab, setActiveTab] = useState('ia');

  // AI Insights avanzados
  const aiInsights = [
    { 
      id: 1, 
      title: "Tendencia de consumo detectada", 
      description: "Se ha detectado un patrón de compra donde el 78% de los clientes que compran cerveza también compran snacks salados. Recomendamos colocar estos productos cercanos y crear promociones combinadas.",
      impact: "alto",
      category: "patrones",
      confidenceScore: 92,
    },
    { 
      id: 2, 
      title: "Predicción de demanda", 
      description: "Basado en tendencias históricas y eventos próximos, se prevé un aumento del 23% en la demanda de bebidas refrescantes para el próximo fin de semana. Sugerimos incrementar stock preventivamente.",
      impact: "medio",
      category: "predicción",
      confidenceScore: 85,
    },
    { 
      id: 3, 
      title: "Optimización de precios", 
      description: "El análisis de elasticidad de precios sugiere que un incremento del 5% en el precio de productos lácteos no afectaría significativamente el volumen de ventas, pero aumentaría el margen en un 4.2%.",
      impact: "medio",
      category: "precios",
      confidenceScore: 78,
    },
    { 
      id: 4, 
      title: "Alerta de producto", 
      description: "El aceite de oliva muestra una tendencia decreciente en ventas (-12%) tras el último ajuste de precio. Recomendamos revisar la estrategia de precios o implementar promociones temporales.",
      impact: "alto",
      category: "alertas",
      confidenceScore: 89,
    },
    { 
      id: 5, 
      title: "Análisis de comportamiento", 
      description: "Se observa que las ventas de café aumentan un 28% entre las 8:00 y 10:00. Recomendamos promociones especiales matutinas para maximizar esta tendencia.",
      impact: "medio",
      category: "patrones",
      confidenceScore: 94,
    },
  ];

  // Recomendaciones del sistema central
  const recommendations = [
    { 
      id: 1, 
      type: "restock", 
      productId: 4, 
      productName: "Aceite de Oliva", 
      message: "Stock bajo detectado. Recomendamos reponer 20 unidades.", 
      priority: "high", 
      timestamp: new Date() 
    },
    { 
      id: 2, 
      type: "trending", 
      productId: 1, 
      productName: "Coca-Cola 500ml", 
      message: "Producto en tendencia. Incrementar stock para satisfacer demanda.", 
      priority: "medium", 
      timestamp: new Date() 
    },
    { 
      id: 3, 
      type: "seasonal", 
      productId: 6, 
      productName: "Galletas Oreo", 
      message: "Patrón estacional detectado. Considerar promoción especial.", 
      priority: "low", 
      timestamp: new Date() 
    },
    { 
      id: 4, 
      type: "pricing", 
      productId: 3, 
      productName: "Leche Entera 1L", 
      message: "Oportunidad de ajuste de precio. La competencia tiene un precio un 8% mayor.", 
      priority: "medium", 
      timestamp: new Date() 
    },
    { 
      id: 5, 
      type: "bundle", 
      productId: 12, 
      productName: "Cerveza Local", 
      message: "Crear oferta combinada con snacks para aumentar ticket medio.", 
      priority: "low", 
      timestamp: new Date() 
    },
  ];

  // Datos para gráficos (simulados)
  const salesTrend = [
    { name: "Lun", ventas: 12, ingresos: 45.80 },
    { name: "Mar", ventas: 19, ingresos: 67.20 },
    { name: "Mié", ventas: 15, ingresos: 52.10 },
    { name: "Jue", ventas: 25, ingresos: 89.50 },
    { name: "Vie", ventas: 32, ingresos: 124.30 },
    { name: "Sáb", ventas: 45, ingresos: 178.90 },
    { name: "Dom", ventas: 38, ingresos: 142.60 },
  ];

  const salesByCategory = [
    { name: "Bebidas", value: 40 },
    { name: "Panadería", value: 30 },
    { name: "Lácteos", value: 25 },
    { name: "Snacks", value: 20 },
    { name: "Conservas", value: 15 },
  ];

  const hourlyTraffic = [
    { hora: "08:00", clientes: 5, ventas: 3 },
    { hora: "09:00", clientes: 12, ventas: 8 },
    { hora: "10:00", clientes: 18, ventas: 15 },
    { hora: "11:00", clientes: 25, ventas: 20 },
    { hora: "12:00", clientes: 30, ventas: 28 },
    { hora: "13:00", clientes: 28, ventas: 25 },
    { hora: "14:00", clientes: 20, ventas: 18 },
    { hora: "15:00", clientes: 15, ventas: 12 },
    { hora: "16:00", clientes: 22, ventas: 19 },
    { hora: "17:00", clientes: 28, ventas: 24 },
    { hora: "18:00", clientes: 35, ventas: 30 },
    { hora: "19:00", clientes: 30, ventas: 27 },
    { hora: "20:00", clientes: 25, ventas: 20 },
    { hora: "21:00", clientes: 15, ventas: 10 },
  ];

  const monthlyRevenue = [
    { mes: "Ene", ingresos: 12500, objetivo: 12000 },
    { mes: "Feb", ingresos: 11800, objetivo: 12000 },
    { mes: "Mar", ingresos: 13200, objetivo: 13000 },
    { mes: "Abr", ingresos: 14500, objetivo: 13000 },
    { mes: "May", ingresos: 15800, objetivo: 14000 },
    { mes: "Jun", ingresos: 16200, objetivo: 14000 },
    { mes: "Jul", ingresos: 17500, objetivo: 15000 },
  ];

  const COLORS = ["#3B82F6", "#60A5FA", "#93C5FD", "#1E40AF", "#2563EB", "#DBEAFE"];

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "high": return "#EF4444"
      case "medium": return "#F59E0B"
      case "low": return "#3B82F6"
      default: return "#6B7280"
    }
  };

  const getRecommendationIcon = (type) => {
    switch (type) {
      case "restock": return TrendingUp
      case "trending": return TrendingUp
      case "seasonal": return Timer
      case "pricing": return Target
      case "bundle": return Award
      default: return Lightbulb
    }
  };

  // Componente para la pestaña de Análisis IA
  const IAAnalysisTab = () => (
    <div className="tab-content">
      <div className="analytics-grid">
        {/* KPIs específicos de IA */}
        <div className="analytics-kpis">
          <div className="analytics-kpi-card">
            <div className="kpi-icon success">
              <Target />
            </div>
            <div className="kpi-content">
              <h3>94.2%</h3>
              <p>Precisión IA</p>
              <span className="kpi-trend positive">+2.1% vs mes pasado</span>
            </div>
          </div>
          <div className="analytics-kpi-card">
            <div className="kpi-icon warning">
              <Zap />
            </div>
            <div className="kpi-content">
              <h3>1.2seg</h3>
              <p>Tiempo Respuesta</p>
              <span className="kpi-trend positive">-0.3seg optimizado</span>
            </div>
          </div>
          <div className="analytics-kpi-card">
            <div className="kpi-icon info">
              <Star />
            </div>
            <div className="kpi-content">
              <h3>87%</h3>
              <p>Recomendaciones Seguidas</p>
              <span className="kpi-trend positive">+5% vs semana pasada</span>
            </div>
          </div>
        </div>

        {/* Insights de IA */}
        <div className="panel full-width">
          <div className="panel-header">
            <div className="panel-icon-wrapper">
              <Brain className="panel-icon" />
            </div>
            <h3>Insights de IA</h3>
          </div>
          <div className="ai-insights-list">
            {aiInsights.map((insight) => (
              <div key={insight.id} className={`ai-insight-card impact-${insight.impact}`}>
                <div className="insight-header">
                  <h4>{insight.title}</h4>
                  <div className="insight-badges">
                    <span className={`impact-badge impact-${insight.impact}`}>
                      Impacto {insight.impact}
                    </span>
                    <span className="confidence-badge">
                      {insight.confidenceScore}% confianza
                    </span>
                    <span className="category-badge">
                      {insight.category}
                    </span>
                  </div>
                </div>
                <p className="insight-description">{insight.description}</p>
                <div className="insight-actions">
                  <button className="action-button">
                    <CheckCircle size={14} />
                    <span>Aplicar</span>
                  </button>
                  <button className="action-button secondary">
                    <LineChart size={14} />
                    <span>Ver datos</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Recomendaciones IA */}
        <div className="panel full-width">
          <div className="panel-header">
            <div className="panel-icon-wrapper">
              <Lightbulb className="panel-icon" />
            </div>
            <h3>Recomendaciones IA</h3>
          </div>
          <div className="recommendations-list enhanced">
            {recommendations.map((rec) => {
              const IconComponent = getRecommendationIcon(rec.type)
              return (
                <div key={rec.id} className={`recommendation-item priority-${rec.priority}`}>
                  <div className="rec-icon-wrapper" style={{ borderColor: getPriorityColor(rec.priority) }}>
                    <IconComponent className="rec-icon" style={{ color: getPriorityColor(rec.priority) }} />
                  </div>
                  <div className="rec-content">
                    <div className="rec-header">
                      <span className="rec-product">{rec.productName}</span>
                      <span className={`rec-priority priority-${rec.priority}`}>{rec.priority}</span>
                    </div>
                    <p className="rec-message">{rec.message}</p>
                    <div className="rec-timestamp">
                      {rec.timestamp.toLocaleTimeString()}
                    </div>
                  </div>
                  <div className="rec-actions">
                    <button className="rec-action-button">
                      <CheckCircle size={14} />
                      <span>Aplicar</span>
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  );

  // Componente para la pestaña de Análisis Generales
  const GeneralAnalysisTab = () => (
    <div className="tab-content">
      <div className="analytics-grid">
        {/* KPIs generales de ventas */}
        <div className="analytics-kpis">
          <div className="analytics-kpi-card">
            <div className="kpi-icon success">
              <TrendingUp />
            </div>
            <div className="kpi-content">
              <h3>S/2,847</h3>
              <p>Ingresos Totales</p>
              <span className="kpi-trend positive">+12.3% vs semana pasada</span>
            </div>
          </div>
          <div className="analytics-kpi-card">
            <div className="kpi-icon warning">
              <Activity />
            </div>
            <div className="kpi-content">
              <h3>156</h3>
              <p>Ventas Totales</p>
              <span className="kpi-trend positive">+8.7% vs promedio</span>
            </div>
          </div>
          <div className="analytics-kpi-card">
            <div className="kpi-icon info">
              <BarChart3 />
            </div>
            <div className="kpi-content">
              <h3>S/18.25</h3>
              <p>Ticket Promedio</p>
              <span className="kpi-trend positive">+3.2% vs mes pasado</span>
            </div>
          </div>
        </div>

        {/* Charts Component */}
        <ChartsComponent 
          salesTrend={salesTrend}
          salesByCategory={salesByCategory}
          hourlyTraffic={hourlyTraffic}
          monthlyRevenue={monthlyRevenue}
          COLORS={COLORS}
        />

        {/* Panel de resumen de métricas */}
        <div className="panel full-width">
          <div className="panel-header">
            <div className="panel-icon-wrapper">
              <BarChart3 className="panel-icon" />
            </div>
            <h3>Resumen de Métricas</h3>
          </div>
          <div className="metrics-summary">
            <div className="metric-row">
              <div className="metric-card">
                <h4>Productos Más Vendidos</h4>
                <div className="metric-list">
                  <div className="metric-item">
                    <span className="product-name">Coca-Cola 500ml</span>
                    <span className="product-sales">45 unidades</span>
                  </div>
                  <div className="metric-item">
                    <span className="product-name">Pan de Molde</span>
                    <span className="product-sales">32 unidades</span>
                  </div>
                  <div className="metric-item">
                    <span className="product-name">Leche Entera 1L</span>
                    <span className="product-sales">28 unidades</span>
                  </div>
                </div>
              </div>
              <div className="metric-card">
                <h4>Horarios Pico</h4>
                <div className="metric-list">
                  <div className="metric-item">
                    <span className="product-name">18:00 - 19:00</span>
                    <span className="product-sales">35 clientes</span>
                  </div>
                  <div className="metric-item">
                    <span className="product-name">12:00 - 13:00</span>
                    <span className="product-sales">30 clientes</span>
                  </div>
                  <div className="metric-item">
                    <span className="product-name">17:00 - 18:00</span>
                    <span className="product-sales">28 clientes</span>
                  </div>
                </div>
              </div>
              <div className="metric-card">
                <h4>Categorías Top</h4>
                <div className="metric-list">
                  <div className="metric-item">
                    <span className="product-name">Bebidas</span>
                    <span className="product-sales">S/875</span>
                  </div>
                  <div className="metric-item">
                    <span className="product-name">Panadería</span>
                    <span className="product-sales">S/623</span>
                  </div>
                  <div className="metric-item">
                    <span className="product-name">Lácteos</span>
                    <span className="product-sales">S/445</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="analytics">
      <header className="content-header">
        <div className="header-info">
          <h2>Estadísticas e Insights</h2>
          <p>Análisis inteligente y métricas de rendimiento</p>
        </div>
      </header>

      {/* Navegación por pestañas */}
      <div className="tabs-container">
        <div className="tabs-nav">
          <button 
            className={`tab-button ${activeTab === 'ia' ? 'active' : ''}`}
            onClick={() => setActiveTab('ia')}
          >
            <Brain className="tab-icon" />
            <span>Análisis IA</span>
          </button>
          <button 
            className={`tab-button ${activeTab === 'general' ? 'active' : ''}`}
            onClick={() => setActiveTab('general')}
          >
            <BarChart3 className="tab-icon" />
            <span>Análisis General</span>
          </button>
        </div>
      </div>

      <div className="analytics-view">
        {activeTab === 'ia' ? <IAAnalysisTab /> : <GeneralAnalysisTab />}
      </div>

      <style jsx>{`
        .analytics {
          padding: 24px;
          background: #f9fafb;
          min-height: 100vh;
        }

        .content-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 24px;
          background: white;
          padding: 20px;
          border-radius: 12px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .header-info h2 {
          font-size: 24px;
          font-weight: 600;
          color: #1f2937;
          margin-bottom: 4px;
        }

        .header-info p {
          color: #6b7280;
          margin: 0;
        }

        /* Estilos de pestañas */
        .tabs-container {
          background: white;
          border-radius: 12px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          margin-bottom: 24px;
          overflow: hidden;
        }

        .tabs-nav {
          display: flex;
          border-bottom: 1px solid #e5e7eb;
        }

        .tab-button {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 16px 24px;
          background: none;
          border: none;
          font-size: 14px;
          font-weight: 500;
          color: #6b7280;
          cursor: pointer;
          transition: all 0.3s ease;
          border-bottom: 3px solid transparent;
        }

        .tab-button:hover {
          background: #f9fafb;
          color: #3b82f6;
        }

        .tab-button.active {
          color: #3b82f6;
          border-bottom-color: #3b82f6;
          background: #f9fafb;
        }

        .tab-icon {
          width: 18px;
          height: 18px;
        }

        .tab-content {
          animation: fadeIn 0.3s ease-in-out;
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .analytics-view {
          max-width: 1600px;
          margin: 0 auto;
        }

        .analytics-grid {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .analytics-kpis {
          display: flex;
          gap: 20px;
          flex-wrap: wrap;
        }

        .analytics-kpi-card {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 16px;
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          transition: all 0.3s ease;
          flex: 1;
          min-width: 250px;
        }

        .analytics-kpi-card:hover {
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          transform: translateY(-2px);
        }

        .analytics-kpi-card .kpi-icon {
          width: 40px;
          height: 40px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .analytics-kpi-card .kpi-icon.success {
          background: rgba(16, 185, 129, 0.1);
          color: #10b981;
        }

        .analytics-kpi-card .kpi-icon.warning {
          background: rgba(245, 158, 11, 0.1);
          color: #f59e0b;
        }

        .analytics-kpi-card .kpi-icon.info {
          background: rgba(59, 130, 246, 0.1);
          color: #3b82f6;
        }

        .analytics-kpi-card .kpi-content h3 {
          font-size: 20px;
          color: #1f2937;
          margin: 0 0 4px 0;
        }

        .analytics-kpi-card .kpi-content p {
          font-size: 14px;
          color: #6b7280;
          margin: 0 0 4px 0;
        }

        .analytics-kpi-card .kpi-trend {
          background: rgba(16, 185, 129, 0.1);
          color: #10b981;
          padding: 2px 8px;
          border-radius: 12px;
          font-size: 12px;
        }

        .panel {
          background: white;
          border-radius: 12px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          border: 1px solid #e5e7eb;
        }

        .full-width {
          width: 100%;
        }

        .panel-header {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 16px 20px;
          border-bottom: 1px solid #e5e7eb;
        }

        .panel-icon-wrapper {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 32px;
          height: 32px;
          background: #3b82f6;
          border-radius: 8px;
        }

        .panel-icon {
          width: 18px;
          height: 18px;
          color: white;
        }

        .panel-header h3 {
          margin: 0;
          font-size: 16px;
          font-weight: 600;
          color: #1f2937;
        }

        /* AI Insights */
        .ai-insights-list {
          padding: 20px;
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
          gap: 16px;
        }

        .ai-insight-card {
          background: #f9fafb;
          border-radius: 8px;
          padding: 20px;
          border-top: 4px solid;
          display: flex;
          flex-direction: column;
          gap: 12px;
          transition: all 0.3s ease;
        }

        .ai-insight-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
        }

        .ai-insight-card.impact-alto {
          border-top-color: #ef4444;
        }

        .ai-insight-card.impact-medio {
          border-top-color: #f59e0b;
        }

        .ai-insight-card.impact-bajo {
          border-top-color: #3b82f6;
        }

        .insight-header {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .insight-header h4 {
          margin: 0;
          font-size: 16px;
          font-weight: 600;
          color: #1f2937;
        }

        .insight-badges {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
        }

        .insight-badges span {
          padding: 4px 8px;
          border-radius: 16px;
          font-size: 11px;
          font-weight: 500;
        }

        .impact-badge {
          background: rgba(59, 130, 246, 0.1);
          color: #3b82f6;
        }

        .impact-badge.impact-alto {
          background: rgba(239, 68, 68, 0.1);
          color: #ef4444;
        }

        .impact-badge.impact-medio {
          background: rgba(245, 158, 11, 0.1);
          color: #f59e0b;
        }

        .confidence-badge {
          background: rgba(16, 185, 129, 0.1);
          color: #10b981;
        }

        .category-badge {
          background: rgba(124, 58, 237, 0.1);
          color: #7c3aed;
        }

        .insight-description {
          color: #6b7280;
          font-size: 14px;
          line-height: 1.5;
          margin: 0;
        }

        .insight-actions {
          display: flex;
          gap: 8px;
          margin-top: 8px;
        }

        .action-button {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 6px 12px;
          border-radius: 6px;
          background: #3b82f6;
          color: white;
          border: none;
          font-size: 12px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .action-button:hover {
          background: #2563eb;
        }

        .action-button.secondary {
          background: white;
          border: 1px solid #3b82f6;
          color: #3b82f6;
        }

        .action-button.secondary:hover {
          background: rgba(59, 130, 246, 0.1);
        }

        /* Recommendations */
        .recommendations-list {
          padding: 20px;
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .recommendations-list.enhanced {
          max-height: none;
        }

        .recommendation-item {
          display: flex;
          gap: 12px;
          padding: 16px;
          background: #f9fafb;
          border-radius: 8px;
          border-left: 4px solid;
        }

        .recommendation-item.priority-high {
          border-left-color: #ef4444;
        }

        .recommendation-item.priority-medium {
          border-left-color: #f59e0b;
        }

        .recommendation-item.priority-low {
          border-left-color: #3b82f6;
        }

        .rec-icon-wrapper {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 32px;
          height: 32px;
          background: white;
          border-radius: 8px;
          border: 2px solid;
          flex-shrink: 0;
        }

        .rec-icon {
          width: 16px;
          height: 16px;
        }

        .rec-content {
          flex: 1;
        }

        .rec-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 8px;
        }

        .rec-product {
          font-weight: 600;
          color: #1f2937;
        }

        .rec-priority {
          padding: 2px 8px;
          border-radius: 12px;
          font-size: 11px;
          font-weight: 600;
          text-transform: uppercase;
        }

        .rec-priority.priority-high {
          background: rgba(239, 68, 68, 0.1);
          color: #ef4444;
        }

        .rec-priority.priority-medium {
          background: rgba(245, 158, 11, 0.1);
          color: #f59e0b;
        }

        .rec-priority.priority-low {
          background: rgba(59, 130, 246, 0.1);
          color: #3b82f6;
        }

        .rec-message {
          margin: 0 0 8px;
          color: #6b7280;
          font-size: 14px;
          line-height: 1.4;
        }

        .rec-timestamp {
          font-size: 12px;
          color: #9ca3af;
        }

        .rec-actions {
          display: flex;
          justify-content: flex-end;
          margin-top: 8px;
        }

        .rec-action-button {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 6px 12px;
          border-radius: 6px;
          background: #3b82f6;
          color: white;
          border: none;
          font-size: 12px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .rec-action-button:hover {
          background: #2563eb;
        }

        /* Métricas generales */
        .metrics-summary {
          padding: 20px;
        }

        .metric-row {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 20px;
        }

        .metric-card {
          background: #f9fafb;
          border-radius: 8px;
          padding: 16px;
          border: 1px solid #e5e7eb;
        }

        .metric-card h4 {
          margin: 0 0 12px 0;
          font-size: 14px;
          font-weight: 600;
          color: #1f2937;
        }

        .metric-list {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .metric-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 8px 0;
          border-bottom: 1px solid #e5e7eb;
        }

        .metric-item:last-child {
          border-bottom: none;
        }

        .product-name {
          font-size: 13px;
          color: #374151;
        }

        .product-sales {
          font-size: 13px;
          font-weight: 600;
          color: #1f2937;
        }

        @media (max-width: 1280px) {
          .ai-insights-list {
            grid-template-columns: 1fr;
          }
          
          .metric-row {
            grid-template-columns: 1fr;
          }
        }

        @media (max-width: 768px) {
          .analytics-kpis {
            flex-direction: column;
          }

          .analytics-kpi-card {
            min-width: auto;
          }
          
          .tabs-nav {
            flex-direction: column;
          }
          
          .tab-button {
            justify-content: center;
          }
        }
      `}</style>
    </div>
  );
};

export default Analytics;